import { useAppNavigate } from "hooks/useAppNavigate";
import React, { useRef, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import OtpInput from "components/Otp";
import LoadingButton from "components/LoadingButton";
import ErrorMessage from "components/ErrorMessage";
import PageTitle from "components/PageTitle";
import PageMeta from "components/PageMeta";
import InfoBanner from "components/InfoBanner";
import LinkButton from "components/LinkButton";

import { approveDeviceCode, finishPasswordlessLogin } from "services/auth.service";
import { setUserData, getFlowState, clearFlowState, setJwtTokens } from "../../storage";
import { useLoginPage } from "hooks/useLoginPage";
import { useText } from "context/TextProvider";
import { trackEvent } from "services/analytics.service";
import { AnalyticsEvent, AnalyticsFlow, AnalyticsMethod, AnalyticsStage, AnalyticsStatus, DeviceGrantStep } from "services/analyticsEvents";
import { useScreenTimeTracking } from "hooks/useScreenTimeTracking";
import { postAuthTokenToNative } from "utils/nativeBridge";

const DeviceGrantOtpVerification = (): JSX.Element => {
  const text = useText();
  const appNavigate = useAppNavigate();
  const { configId, loginPageConfig } = useLoginPage();
  useScreenTimeTracking(AnalyticsEvent.TIME_ON_DEVICE_OTP_SCREEN);
  const flowState = getFlowState(configId);
  const email = flowState.email || "";
  const code = flowState.code || "";
  const [otp, setOtp] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const enteredOtpScreenAt = useRef(Date.now());

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");
    trackEvent(AnalyticsEvent.DEVICE_TIME_TO_ENTER_OTP, { duration_ms: Date.now() - enteredOtpScreenAt.current });
    try {
      const data = await finishPasswordlessLogin({
        code,
        oneTimeCode: otp,
        url: loginPageConfig?.urls?.submit_login_otp,
      });
      setUserData(data);
      const { user, ...tokens } = data;
      setJwtTokens(tokens);
      setMessage(text.deviceGrant.otpVerification.verifiedSuccess);
      trackEvent(AnalyticsEvent.DEVICE_PASSWORDLESS_LOGIN_SUCCEEDED, { method: AnalyticsMethod.PASSWORDLESS, flow: AnalyticsFlow.DEVICE_GRANT, status: AnalyticsStatus.SUCCEEDED });
      if (flowState.loginStartedAt) {
        trackEvent(AnalyticsEvent.DEVICE_TIME_TO_LOGIN, { duration_ms: Date.now() - flowState.loginStartedAt });
      }
      const jwt = data.token;
      if (data.profileUpdate?.showUpdateScreen) {
        clearFlowState(configId);
        appNavigate(`/device-grant-flow/profile-update`, {state: { email } });
        return;
      }
      const userCode = sessionStorage.getItem("device_grant_user_code") ?? undefined;
      await approveDeviceCode({
        user_code: userCode,
        access_token: jwt,
        url: loginPageConfig?.urls?.approve_device_code,
      });
      trackEvent(AnalyticsEvent.APPROVE_DEVICE_CODE_SUCCEEDED, { step: DeviceGrantStep.APPROVE_DEVICE_CODE, status: AnalyticsStatus.SUCCEEDED });
      clearFlowState(configId);
      postAuthTokenToNative({
        token: jwt,
        refreshToken: data.refreshToken,
        tokenExpirationInstant: data.tokenExpirationInstant,
        refreshUrl: loginPageConfig?.urls?.refresh_url,
      });
      appNavigate(`/device-grant-flow/complete`);
    } catch (err) {
      setMessage(text.deviceGrant.otpVerification.verificationFailed);
      trackEvent(AnalyticsEvent.DEVICE_PASSWORDLESS_LOGIN_FAILED, {
        method: AnalyticsMethod.PASSWORDLESS,
        flow: AnalyticsFlow.DEVICE_GRANT,
        status: AnalyticsStatus.FAILED,
        stage: AnalyticsStage.OTP,
      });
      trackEvent(AnalyticsEvent.DEVICE_OTP_ERROR, { message: err instanceof Error ? err.message : undefined });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageMeta name="Device Grant OTP Verification" />
      <PageTitle>{text.deviceGrant.otpVerification.title}</PageTitle>
      <InfoBanner>
        <Typography component="div" sx={{ fontWeight: 500, mb: "0.25rem" }}>
          {text.deviceGrant.otpVerification.codeSentPrefix}{" "}
          <Typography component="span" sx={{ fontWeight: 600 }}>
            {email}
          </Typography>
          .
        </Typography>
        <Typography component="div" sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
          {text.deviceGrant.otpVerification.checkSpamFolder}
        </Typography>
      </InfoBanner>
      <OtpInput otp={otp} setOtp={setOtp} />
      <LoadingButton
        loading={loading}
        onClick={handleSubmit}
        disabled={otp.length !== 6}
        sx={{
          width: "100%",
          height: "3.5rem",
          mb: "1.5rem",
        }}
      >
        {text.deviceGrant.otpVerification.submitButton}
      </LoadingButton>
      {message && <ErrorMessage sx={{ mb: "1.5rem" }}>{message}</ErrorMessage>}
      <Box sx={{ textAlign: "center" }}>
        <Typography component="span" sx={{ color: "text.secondary", fontSize: "0.875rem" }}>
          {text.deviceGrant.otpVerification.troubleLoggingIn}
        </Typography>
        <LinkButton
          onClick={() => appNavigate(`/device-grant-flow/password-login`)}
          sx={{ fontSize: "0.875rem", fontWeight: 600 }}
        >
          {text.deviceGrant.otpVerification.loginWithPasswordButton}
        </LinkButton>
      </Box>
    </>
  );
};

export default DeviceGrantOtpVerification;
